import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../../components/ui/select";
import MobileFormDialog from "../../../components/ui/mobile-form-dialog";
import { HHMMInput } from "../../../components/ui/hhmm-input";
import { AircraftRegistrationCombobox } from "../../../components/ui/aircraft-combobox";
import { useAircraftNumberSuggestion } from "./useAircraftNumberSuggestion";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { API } from "../../../App";
import { useAircraftGate } from "./AircraftGateDialog";
import { confirmarSemReserva } from "./reservationWarning";
import {
  DEFAULT_AIRCRAFT, SOLO_FLIGHT_TYPES, FLIGHT_TYPE_LABELS, fmtDate, minutesToHM,
} from "./diUtils";

export default function FlightForm({ instructorId, instructor, flight, onClose, onSaved }) {
  const isEdit = !!flight?.id;
  const { checkGate, gateElement } = useAircraftGate();
  const [form, setForm] = useState(() => ({
    flight_date: flight?.flight_date || new Date().toISOString().slice(0, 10),
    pic_minutes: flight?.pic_minutes ?? 60,
    instructor_minutes: flight?.instructor_minutes ?? 0,
    flight_type: flight?.flight_type || "instrucao",
    aircraft_registration: flight?.aircraft_registration ?? DEFAULT_AIRCRAFT,
    navigation_log_number: flight?.navigation_log_number || "",
    service_number: flight?.service_number || "",
    authorized_by_instructor_id: flight?.authorized_by_instructor_id || "",
    notes: flight?.notes || "",
    ground_run_minutes: flight?.ground_run_minutes ?? 0,
    landings: flight?.landings ?? "",
  }));
  const { hasSuggestion, markTouched } = useAircraftNumberSuggestion({ aircraftRegistration: form.aircraft_registration, isEdit, setForm });
  const [companion, setCompanion] = useState(() => ({
    kind: flight?.companion_kind || "",
    id: flight?.companion_id || "",
    name: flight?.companion_name || "",
    license: flight?.companion_license || "",
  }));
  const [pilots, setPilots] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [externals, setExternals] = useState([]);
  const [swapBusy, setSwapBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [authCandidates, setAuthCandidates] = useState([]);
  const isRestricted = instructor?.license_status === "R";
  const isSolo = SOLO_FLIGHT_TYPES.has(form.flight_type);
  const requiresAuthorizer = isRestricted && isSolo;
  const isInstruction = form.flight_type === "instrucao";
  const isUniformizacao = form.flight_type === "uniformizacao";

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const [pr, ir, er] = await Promise.all([
          axios.get(`${API}/di/pilots`, { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${API}/di/instructors`, { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${API}/di/flights/_companion_externals`, { withCredentials: true }).catch(() => ({ data: [] })),
        ]);
        if (cancel) return;
        setPilots(pr.data || []);
        setInstructors(ir.data || []);
        setExternals(er.data || []);
      } catch { /* silent */ }
    })();
    return () => { cancel = true; };
  }, []);

  useEffect(() => {
    if (!pilots.length || !companion.id || companion.kind !== "pilot") return;
    const p = pilots.find(x => x.id === companion.id);
    if (p?.kind === "student") setCompanion(c => ({ ...c, kind: "student" }));
  }, [pilots, companion.id, companion.kind]);

  useEffect(() => {
    if (isEdit) return;
    setCompanion((c) => {
      if (c.id || c.name) return c;
      if (isUniformizacao) return { ...c, kind: "instructor" };
      if (isInstruction || isSolo) return { ...c, kind: "student" };
      return { kind: "", id: "", name: "", license: "" };
    });
  }, [form.flight_type, isEdit, isInstruction, isUniformizacao, isSolo]);

  useEffect(() => {
    if (!requiresAuthorizer) return;
    let cancel = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/di/instructors`, { withCredentials: true });
        if (cancel) return;
        setAuthCandidates((r.data || []).filter(i => i.id !== instructorId && i.license_status !== "R"));
      } catch { /* silent */ }
    })();
    return () => { cancel = true; };
  }, [requiresAuthorizer, instructorId]);

  const selectedPilot = (companion.kind === "pilot" || companion.kind === "student")
    ? pilots.find(p => p.id === companion.id) : null;
  const otherInstructors = instructors.filter(i => i.id !== instructorId);
  const setCompanionKind = (kind) => setCompanion({ kind, id: "", name: "", license: "" });

  const submit = async () => {
    if (form.instructor_minutes > form.pic_minutes) { toast.error("Minutos como Instrutor não podem exceder os minutos PIC"); return; }
    if (!(form.navigation_log_number || "").trim()) { toast.error("Diário de navegação é obrigatório."); return; }
    if (!(form.service_number || "").trim()) { toast.error("Nº de serviço é obrigatório."); return; }
    if (!(form.aircraft_registration || "").trim()) { toast.error("A aeronave é obrigatória."); return; }
    if (requiresAuthorizer && !form.authorized_by_instructor_id) { toast.error("Indica o instrutor sem restrição que autorizou este voo solo."); return; }
    if (isInstruction && companion.kind === "") { toast.error("Voos de instrução requerem identificar quem voou em DC (aluno, piloto ou avulso)."); return; }
    if (isSolo && companion.kind !== "pilot" && companion.kind !== "student") { toast.error("Voos a solo requerem indicar o aluno acompanhante."); return; }
    if (isUniformizacao && (companion.kind === "" || (companion.kind === "instructor" && !companion.id))) { toast.error("Voos de uniformização requerem indicar o instrutor supervisor a bordo."); return; }

    // Validações do acompanhante antes de perguntar pelo estado da aeronave (para não perguntar duas vezes)
    if ((companion.kind === "pilot" || companion.kind === "student") && !companion.id) { toast.error("Escolhe o piloto/aluno a bordo ou limpa o campo."); return; }
    if (companion.kind === "external" && !(companion.name || "").trim()) { toast.error("Indica o nome do piloto avulso ou limpa o campo."); return; }
    // Aeronave com pendências: avisa e pede confirmação, mas nunca impede o registo
    if (!isEdit && !(await checkGate(form.aircraft_registration, form.flight_date))) return;
    if (!isEdit && !(await confirmarSemReserva({ aircraft: form.aircraft_registration, date: form.flight_date, instructorId }))) return;

    let companionPayload = { companion_kind: null, companion_id: null, companion_name: null, companion_license: null };
    if (companion.kind === "pilot" || companion.kind === "student") {
      if (!companion.id) { toast.error("Escolhe o piloto/aluno a bordo ou limpa o campo."); return; }
      companionPayload = { companion_kind: "pilot", companion_id: companion.id, companion_name: companion.name || null, companion_license: null };
    } else if (companion.kind === "instructor") {
      if (companion.id) companionPayload = { companion_kind: "instructor", companion_id: companion.id, companion_name: companion.name || null, companion_license: null };
    } else if (companion.kind === "external") {
      const nm = (companion.name || "").trim();
      if (!nm) { toast.error("Indica o nome do piloto avulso ou limpa o campo."); return; }
      const licenseText = (companion.license || "").trim();
      try {
        const avulsoRes = await axios.post(`${API}/di/pilots/avulso`, { name: nm, notes: licenseText || "" }, { withCredentials: true });
        const avulso = avulsoRes.data;
        if (licenseText && !(avulso.notes || "").includes(licenseText)) {
          const updatedNotes = avulso.notes ? `${avulso.notes}\n${licenseText}` : licenseText;
          try { await axios.put(`${API}/di/pilots/${avulso.id}`, { notes: updatedNotes }, { withCredentials: true }); } catch { /* silent */ }
        }
        companionPayload = { companion_kind: "pilot", companion_id: avulso.id, companion_name: avulso.name || nm, companion_license: licenseText || null };
      } catch {
        companionPayload = { companion_kind: "external", companion_id: null, companion_name: nm, companion_license: (companion.license || "").trim() || null };
      }
    }
    setSaving(true);
    try {
      const payload = {
        ...form, ...companionPayload,
        pic_minutes: parseInt(form.pic_minutes, 10),
        instructor_minutes: parseInt(form.instructor_minutes, 10),
        landings: form.landings === "" || form.landings == null ? null : Number(form.landings),
        authorized_by_instructor_id: requiresAuthorizer ? form.authorized_by_instructor_id : (form.authorized_by_instructor_id || null),
        supervisor_instructor_id: isUniformizacao && companion.kind === "instructor" && companion.id ? companion.id : null,
      };
      if (isEdit) {
        await axios.put(`${API}/di/instructors/${instructorId}/flights/${flight.id}`, payload, { withCredentials: true });
        toast.success("Voo atualizado");
      } else {
        await axios.post(`${API}/di/instructors/${instructorId}/flights`, payload, { withCredentials: true });
        toast.success("Voo registado");
      }
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a guardar voo");
    } finally { setSaving(false); }
  };

  const swapPic = async () => {
    if (!isEdit) return;
    if (!window.confirm("Trocar PIC?\n\nO instrutor atual passa a ser o acompanhante e o segundo instrutor passa a ser PIC.")) return;
    setSwapBusy(true);
    try {
      await axios.post(`${API}/di/flights/${flight.id}/swap-pic`, {}, { withCredentials: true });
      toast.success("PIC trocado com sucesso");
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro a trocar PIC"); }
    finally { setSwapBusy(false); }
  };

  return (
    <MobileFormDialog open onOpenChange={(o) => !o && onClose()} title={isEdit ? "Editar voo" : "Registar voo"}
      description={instructor?.name ? `Instrutor: ${instructor.name}${instructor.pu_number ? ` (PU ${instructor.pu_number})` : ""}` : null}
      size="xl" onSubmit={submit} submitLabel={isEdit ? "Atualizar" : "Registar"} submitting={saving} testId="di-flight-form-dialog">
      {gateElement}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="di-flight-form">
        <div>
          <Label>Data *</Label>
          <Input type="date" value={form.flight_date} onChange={(e) => setForm({ ...form, flight_date: e.target.value })} data-testid="di-flight-date" />
        </div>
        <div>
          <Label>Tipo</Label>
          <Select value={form.flight_type} onValueChange={(v) => setForm({ ...form, flight_type: v })}>
            <SelectTrigger data-testid="di-flight-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(FLIGHT_TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Tempo PIC * (HH:MM)</Label>
          <HHMMInput value={form.pic_minutes} onChange={(mins) => setForm({ ...form, pic_minutes: mins ?? 0 })} max={1440} data-testid="di-flight-pic" placeholder="1:00" />
        </div>
        <div>
          <Label>Tempo como Instrutor (HH:MM)</Label>
          <HHMMInput value={form.instructor_minutes} onChange={(mins) => setForm({ ...form, instructor_minutes: mins ?? 0 })} max={1440} data-testid="di-flight-instr" placeholder="0:00" />
        </div>
        <div>
          <Label>Aeronave (matrícula) *</Label>
          <AircraftRegistrationCombobox value={form.aircraft_registration} onChange={(v) => setForm({ ...form, aircraft_registration: v })} placeholder={DEFAULT_AIRCRAFT} data-testid="flight-form-aircraft" />
        </div>
        <div>
          <Label>Diário de navegação *</Label>
          <Input value={form.navigation_log_number} onChange={(e) => { markTouched("nav"); setForm({ ...form, navigation_log_number: e.target.value }); }} data-testid="di-flight-nav-log" required />
        </div>
        <div>
          <Label>Nº de serviço *</Label>
          <Input value={form.service_number} onChange={(e) => { markTouched("svc"); setForm({ ...form, service_number: e.target.value }); }} data-testid="di-flight-svc-num" required />
          {hasSuggestion && !isEdit && <p className="text-[10px] text-slate-400 mt-1" data-testid="di-flight-number-hint">Sugerido pela aeronave: diário do último voo e nº de serviço seguinte. Podes alterar.</p>}
        </div>
        {requiresAuthorizer && (
          <div className="sm:col-span-2 p-3 rounded-md border border-amber-500/40 bg-amber-500/5">
            <Label className="text-amber-300 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" />Autorizado por (instrutor sem restrição) *</Label>
            <p className="text-[11px] text-slate-400 mt-1 mb-2">Como instrutor com restrição (R), este voo solo precisa de ser autorizado.</p>
            <Select value={form.authorized_by_instructor_id || ""} onValueChange={(v) => setForm({ ...form, authorized_by_instructor_id: v })}>
              <SelectTrigger data-testid="di-flight-authorizer"><SelectValue placeholder="Selecionar instrutor autorizador..." /></SelectTrigger>
              <SelectContent>
                {authCandidates.length === 0 && <div className="px-2 py-3 text-xs text-slate-500 italic">Sem instrutores sem restrição disponíveis.</div>}
                {authCandidates.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} (PU {c.pu_number}) {c.license_status === "S" ? "· S" : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        {/* Companion block */}
        <div className="sm:col-span-2 p-3 rounded-md border border-cockpit-border bg-slate-900/30 space-y-2" data-testid="di-flight-companion-block">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <Label className="text-slate-300 text-xs uppercase tracking-wider">
              {isUniformizacao ? "2º Instrutor a bordo" : isInstruction ? <span>Outro piloto a bordo <span className="text-red-400">*</span></span> : "Outro piloto a bordo"}
            </Label>
            <div className="flex gap-1 text-[11px] flex-wrap">
              {!isInstruction && <button type="button" onClick={() => setCompanionKind("")} className={`px-2 py-0.5 rounded border ${companion.kind === "" ? "bg-slate-700 border-slate-500 text-slate-100" : "border-slate-700 text-slate-400 hover:text-slate-200"}`} data-testid="di-companion-kind-none">Nenhum</button>}
              {!isUniformizacao && <button type="button" onClick={() => setCompanionKind("student")} className={`px-2 py-0.5 rounded border ${companion.kind === "student" ? "bg-emerald-700 border-emerald-400 text-white" : "border-slate-700 text-slate-400 hover:text-slate-200"}`} data-testid="di-companion-kind-student">Aluno</button>}
              {!isUniformizacao && <button type="button" onClick={() => setCompanionKind("pilot")} className={`px-2 py-0.5 rounded border ${companion.kind === "pilot" ? "bg-blue-600 border-blue-400 text-white" : "border-slate-700 text-slate-400 hover:text-slate-200"}`} data-testid="di-companion-kind-pilot">Piloto</button>}
              <button type="button" onClick={() => setCompanionKind("instructor")} className={`px-2 py-0.5 rounded border ${companion.kind === "instructor" ? "bg-orange-600 border-orange-400 text-white" : "border-slate-700 text-slate-400 hover:text-slate-200"}`} data-testid="di-companion-kind-instructor">Instrutor</button>
              {!isUniformizacao && <button type="button" onClick={() => setCompanionKind("external")} className={`px-2 py-0.5 rounded border ${companion.kind === "external" ? "bg-slate-600 border-slate-400 text-white" : "border-slate-700 text-slate-400 hover:text-slate-200"}`} data-testid="di-companion-kind-external">Avulso</button>}
            </div>
          </div>
          {isInstruction && companion.kind === "" && <p className="text-[10px] text-amber-400">Selecciona quem voou em DC neste voo de instrução.</p>}
          {companion.kind === "student" && (
            <div>
              <Select value={companion.id || ""} onValueChange={(v) => { const p = pilots.find(x => x.id === v); setCompanion({ kind: "student", id: v, name: p?.name || "", license: "" }); }}>
                <SelectTrigger data-testid="di-companion-student-select"><SelectValue placeholder={pilots.filter(p => p.kind === "student").length ? "Escolher aluno..." : "Sem alunos registados"} /></SelectTrigger>
                <SelectContent>{pilots.filter(p => p.kind === "student").map(p => <SelectItem key={p.id} value={p.id}>{p.name} {p.pu_number ? `· ${p.pu_number}` : ""}</SelectItem>)}</SelectContent>
              </Select>
              {(() => { const sp = pilots.find(p => p.id === companion.id); return sp?.flight_stats ? (<div className="text-[11px] text-slate-400 mt-1.5 flex items-center gap-3 flex-wrap"><span className="text-emerald-300 font-mono">DC: {minutesToHM(sp.flight_stats.dc_minutes || 0)}</span><span>{sp.flight_stats.flights || 0} voo(s)</span>{sp.student_progress && <span className={sp.student_progress.ready ? "text-emerald-400" : "text-amber-400"}>{minutesToHM(sp.student_progress.dc_minutes)}/{minutesToHM(sp.student_progress.dc_target_minutes)} DC</span>}</div>) : null; })()}
              <p className="text-[10px] text-slate-500 mt-1">Voo de instrução: as horas contam como DC para o aluno.</p>
            </div>
          )}
          {companion.kind === "pilot" && (
            <div>
              <Select value={companion.id || ""} onValueChange={(v) => { const p = pilots.find(x => x.id === v); setCompanion({ kind: "pilot", id: v, name: p?.name || "", license: "" }); }}>
                <SelectTrigger data-testid="di-companion-pilot-select"><SelectValue placeholder={pilots.filter(p => p.kind !== "student").length ? "Escolher piloto..." : "Sem pilotos registados"} /></SelectTrigger>
                <SelectContent>{pilots.filter(p => p.kind !== "student").map(p => <SelectItem key={p.id} value={p.id}>{p.name} {p.license_type ? `· ${p.license_type}` : ""} {p.pu_number ? `· ${p.pu_number}` : ""}{p.kind === "avulso" ? " (avulso)" : ""}</SelectItem>)}</SelectContent>
              </Select>
              {selectedPilot?.flight_stats && (<div className="text-[11px] text-slate-400 mt-1.5 flex items-center gap-3 flex-wrap" data-testid="di-companion-dc-stats"><span className="text-emerald-300 font-mono">DC: {minutesToHM(selectedPilot.flight_stats.dc_minutes || 0)}</span><span>{selectedPilot.flight_stats.flights || 0} voo(s)</span>{selectedPilot.flight_stats.last_flight && <span>Último: {fmtDate(selectedPilot.flight_stats.last_flight)}</span>}</div>)}
              {isInstruction && <p className="text-[10px] text-slate-500 mt-1">Voo de instrução: o tempo PIC deste voo conta como Duplo Comando (DC) para o piloto seleccionado.</p>}
            </div>
          )}
          {companion.kind === "instructor" && (
            <div>
              <Select value={companion.id || ""} onValueChange={(v) => { const i = otherInstructors.find(x => x.id === v); setCompanion({ kind: "instructor", id: v, name: i?.name || "", license: "" }); }}>
                <SelectTrigger data-testid="di-companion-instructor-select"><SelectValue placeholder={otherInstructors.length ? "Escolher instrutor..." : "Sem outros instrutores"} /></SelectTrigger>
                <SelectContent>{otherInstructors.map(i => <SelectItem key={i.id} value={i.id}>{i.name} (PU {i.pu_number}) {i.license_status === "R" ? "· R" : ""}</SelectItem>)}</SelectContent>
              </Select>
              {isUniformizacao && <p className="text-[10px] text-amber-300/80 mt-1">No fim da uniformização, podes usar "Trocar PIC" para que este instrutor passe a ser o PIC do voo.</p>}
            </div>
          )}
          {companion.kind === "external" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <Input value={companion.name} onChange={(e) => setCompanion({ ...companion, kind: "external", id: "", name: e.target.value })} placeholder="Nome do piloto avulso" list={`companion-externals-${instructorId}`} data-testid="di-companion-external-name" />
                <datalist id={`companion-externals-${instructorId}`}>{externals.map(e => <option key={`${e.name}|${e.license}`} value={e.name}>{e.license ? e.license : ""}</option>)}</datalist>
              </div>
              <Input value={companion.license} onChange={(e) => setCompanion({ ...companion, license: e.target.value })} placeholder="Licença (opcional)" data-testid="di-companion-external-license" />
            </div>
          )}
        </div>
        {/* Tempo em solo ANTES das notas */}
        <div>
          <Label className="flex items-center gap-1">
            Tempo motor em solo (HH:MM)
            <span className="text-[10px] text-slate-500 font-normal ml-1">— aquecimento, taxi, testes (opcional)</span>
          </Label>
          <HHMMInput value={form.ground_run_minutes ?? 0} onChange={(mins) => setForm({ ...form, ground_run_minutes: mins ?? 0 })} placeholder="0:00" data-testid="di-flight-ground-run" />
        </div>
        <div>
          <Label>Aterragens neste voo <span className="text-[10px] text-slate-500 font-normal">— opcional; sem valor conta 1</span></Label>
          <Input type="number" min={0} max={99} value={form.landings} onChange={(e) => setForm({ ...form, landings: e.target.value })} placeholder="ex.: 4" data-testid="di-flight-landings" />
        </div>
        <div className="sm:col-span-2">
          <Label>Notas</Label>
          <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
        </div>
      </div>
      {isEdit && isUniformizacao && flight?.companion_kind === "instructor" && flight?.companion_id && (
        <div className="pt-2 border-t border-slate-700/60 flex justify-start">
          <Button variant="outline" className="border-amber-500/50 text-amber-300 hover:bg-amber-500/10" disabled={swapBusy} onClick={swapPic} data-testid="di-flight-swap-pic">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${swapBusy ? "animate-spin" : ""}`} />
            {swapBusy ? "A trocar..." : "Trocar PIC com 2º instrutor"}
          </Button>
        </div>
      )}
    </MobileFormDialog>
  );
}
